import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Cabine } from "../componentes/FerramentaUi";
import {
  Bloco,
  CAMPO_CLARO,
  CAMPO_NAVY,
  CampoMoeda,
  GovForaDoAr,
  LinhaDeTributo,
  NumeroHeroi,
  RecusaDoGov,
  Rotulo,
  SeloSimulado,
} from "../componentes/CalculadorasUi";
import { chamar } from "../lib/calculadoras";
import {
  anoDoDia,
  emReais,
  fatoGeradorDoDia,
  lerSplit,
  porFornecedor,
  type ResultadoSplit,
} from "../lib/montarCalculo";
import { rolarAte } from "../lib/rolarPara";
import { BASE } from "./Calculadoras";

// A calculadora do Split Payment Simplificado.
//
// ---------------------------------------------------------------------------
// Os TRÊS defeitos que esta tela existe para não repetir
// ---------------------------------------------------------------------------
// A versão do Bubble desta calculadora não funcionava, e não era um erro só.
// Medido contra a API em 14/09, com o payload que ela mandava:
//
//   1. **A URL.** Ela chamava `/api-split/split`, que responde 404. O endpoint
//      é `/api-split/split-payment`. Isso agora é uma linha da lista de rotas
//      da Edge Function, e não uma string solta num formulário.
//
//   2. **A data.** Ela mandava `2026-01-01`, e o gov responde 422: *"O Split
//      Payment Simplificado não está previsto para ser implementado antes de
//      2027."* Por isso esta tela nasce com a data em 2027 e recusa antes de
//      gastar a ida.
//
//   3. **O fornecedor.** Ela usava o CNPJ `5F5CNBWQ402521`, que não está na
//      base do piloto — e aí o gov responde **HTTP 200 com todos os valores
//      zerados**, sem erro nenhum. A tela do Bubble mostraria "R$ 0,00 de CBS"
//      como se fosse retenção. É o pior desfecho possível numa calculadora:
//      número errado com cara de certo. Aqui a lista dos 31 fornecedores
//      cadastrados fica AO LADO do campo, e o resultado zerado é lido como
//      "este CPF/CNPJ não tem percentual cadastrado".
//
// ---------------------------------------------------------------------------
// Por que a lista de cadastrados aparece na tela
// ---------------------------------------------------------------------------
// Porque sem ela a ferramenta é indemonstrável. O Split Simplificado depende de
// um percentual que a Receita cadastra por fornecedor, e no piloto só existem
// 31 — nenhum deles um CNPJ real que o aluno tenha à mão. Sem a lista, todo
// primeiro uso devolve zero, e o aluno conclui que a calculadora está quebrada.

type Cadastrado = {
  cpfCnpj: string;
  aliquotaCbs: number;
  aliquotaIbsUf: number;
  aliquotaIbsMun: number;
  inicioVigencia: string;
};

type Estado =
  | { fase: "vazio" }
  | { fase: "calculando" }
  | { fase: "pronto"; r: ResultadoSplit; simulado: number | null; aviso: string | null; guardado: boolean; motivoDoRegistro: string | null }
  | { fase: "recusa"; titulo: string; detalhe: string }
  | { fase: "indisponivel"; motivo: string };

/**
 * A data de partida.
 *
 * 2027 é o primeiro ano que o gov aceita (medido: antes disso, 422 dizendo que
 * o regime "não está previsto para ser implementado"). Nasce em junho, e não em
 * 1º de janeiro, para a tela não sugerir justamente a fronteira — o dia em que
 * uma regra começa é o pior dia para ensinar com ela.
 */
const DATA_PADRAO = "2027-06-15";

export default function CalcSimplificado() {
  const [dia, setDia] = useState(DATA_PADRAO);
  const [base, setBase] = useState<number | null>(null);
  const [cpfCnpj, setCpfCnpj] = useState("");
  const [rotulo, setRotulo] = useState("");
  const [estado, setEstado] = useState<Estado>({ fase: "vazio" });
  const [cadastrados, setCadastrados] = useState<Cadastrado[] | null>(null);
  const resultadoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void chamar<Cadastrado[]>("split-cadastrados").then((r) => {
      if (r.situacao === "ok" && Array.isArray(r.dados)) setCadastrados(r.dados);
    });
  }, []);

  const ano = anoDoDia(dia);
  const falta: string[] = [];
  if (ano === null) falta.push("a data do pagamento");
  else if (ano < 2027) falta.push("uma data a partir de 2027");
  if (base === null || base <= 0) falta.push("o valor da operação");
  // O gov aceita CPF (11 dígitos) e o CNPJ alfanumérico novo (12 caracteres
  // alfanuméricos + 2 dígitos). A tela confere o formato antes de gastar a ida.
  const documento = cpfCnpj.trim().toUpperCase();
  if (!/^(\d{11}|[A-Z0-9]{12}\d{2})$/.test(documento)) {
    falta.push("um CPF (11 dígitos) ou CNPJ (14 caracteres) válido");
  }

  async function calcular() {
    if (falta.length > 0) return;
    setEstado({ fase: "calculando" });
    const corpo = { baseCalculo: base, cpfCnpj: documento, fatoGerador: fatoGeradorDoDia(dia) };
    const r = await chamar("split-payment", {
      corpo,
      guardar: { calculadora: "simplificado", rotulo: rotulo.trim() || null },
    });

    if (r.situacao === "ok") {
      setEstado({
        fase: "pronto",
        r: lerSplit(r.dados, base),
        simulado: r.simulado,
        aviso: r.aviso,
        guardado: r.guardado,
        motivoDoRegistro: r.motivoDoRegistro,
      });
    } else if (r.situacao === "recusa") {
      setEstado({ fase: "recusa", titulo: r.titulo, detalhe: r.detalhe });
    } else {
      setEstado({ fase: "indisponivel", motivo: "motivo" in r ? r.motivo : "falha" });
    }
    requestAnimationFrame(() => rolarAte(resultadoRef.current));
  }

  return (
    <>
      <Cabine
        trilha={{ para: `${BASE}${window.location.search}`, rotulo: "Calculadoras" }}
        rotulo="Calculadora"
        titulo="Simplificado"
        descricao="O Split Payment Simplificado: quanto o adquirente retém de IBS e CBS no momento do pagamento, e quanto sobra para o fornecedor."
        rodape={
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Rotulo navy para="sp-data">
                Data do pagamento
              </Rotulo>
              <input
                id="sp-data"
                type="date"
                min="2027-01-01"
                className={CAMPO_NAVY}
                value={dia}
                onChange={(e) => setDia(e.target.value)}
              />
            </div>
            <div>
              <Rotulo navy para="sp-base">
                Valor da operação
              </Rotulo>
              <CampoMoeda id="sp-base" navy valor={base} aoMudar={setBase} />
            </div>
            <div>
              <Rotulo navy para="sp-doc">
                CPF ou CNPJ do fornecedor
              </Rotulo>
              <input
                id="sp-doc"
                className={`${CAMPO_NAVY} tabular-nums`}
                placeholder="sem pontuação"
                maxLength={14}
                value={cpfCnpj}
                onChange={(e) => setCpfCnpj(e.target.value.replace(/[^A-Za-z0-9]/g, ""))}
              />
            </div>
          </div>
        }
      />

      <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_22rem]">
        <div className="flex flex-col gap-5">
          <Bloco
            titulo="Fornecedores cadastrados no piloto"
            descricao="O percentual do Split Simplificado é cadastrado pela Receita por fornecedor. No ambiente de piloto existem apenas estes — um CPF/CNPJ fora da lista devolve o cálculo zerado, sem erro. Clique em um para usá-lo."
          >
            {cadastrados === null ? (
              <p className="text-sm text-cinza">Carregando a lista da Receita…</p>
            ) : cadastrados.length === 0 ? (
              <p className="text-sm text-cinza">
                A Receita não devolveu nenhum fornecedor cadastrado agora. Você ainda pode
                calcular informando um CPF/CNPJ — se ele não estiver cadastrado, o resultado
                virá zerado e a tela vai dizer isso.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-borda text-xs text-cinza">
                      <th className="py-2 font-semibold">CPF / CNPJ</th>
                      <th className="py-2 font-semibold">CBS</th>
                      <th className="py-2 font-semibold">IBS UF</th>
                      <th className="py-2 font-semibold">IBS Mun.</th>
                      <th className="py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {porFornecedor(cadastrados, dia).map((c) => (
                      <tr
                        key={c.cpfCnpj}
                        data-fornecedor={c.cpfCnpj}
                        className="border-b border-borda last:border-0"
                      >
                        <td className="py-2 font-mono text-xs text-tinta">
                          {c.cpfCnpj}
                          {/* O fornecedor cujo percentual ainda não começou fica
                              na lista, com o aviso: sumir com ele ao trocar a
                              data faria o aluno achar que ele deixou de existir. */}
                          {c.vigenteEm && (
                            <span className="ml-2 font-sans text-[11px] font-semibold text-alerta-tinta">
                              a partir de {c.vigenteEm}
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-xs text-cinza tabular-nums">
                          {formatarPercentual(c.aliquotaCbs)}
                        </td>
                        <td className="py-2 text-xs text-cinza tabular-nums">
                          {formatarPercentual(c.aliquotaIbsUf)}
                        </td>
                        <td className="py-2 text-xs text-cinza tabular-nums">
                          {formatarPercentual(c.aliquotaIbsMun)}
                        </td>
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            className="rounded-lg border border-borda px-3 py-1 text-xs font-semibold text-tinta transition hover:border-laranja"
                            onClick={() => {
                              setCpfCnpj(c.cpfCnpj);
                              // A data também: usar um fornecedor com vigência
                              // em 2028 numa operação de 2027 devolve zero, que
                              // é exatamente a confusão que esta tela evita.
                              if (c.vigenteEm) setDia(c.vigenteEm);
                            }}
                          >
                            Usar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-3 text-xs leading-relaxed text-cinza">
                  Uma linha por fornecedor, com o percentual que vale na data escolhida acima.
                  A Receita cadastra percentuais por período — trocar a data pode mudar estes
                  números.
                </p>
              </div>
            )}
          </Bloco>

          <Bloco
            titulo="Como o Split Simplificado funciona"
            descricao="O que esta calculadora mostra, em uma frase."
          >
            <p className="text-sm leading-relaxed text-cinza">
              No Split Payment, o IBS e a CBS são separados do pagamento e recolhidos
              diretamente ao Fisco. No modo <strong className="text-tinta">Simplificado</strong>,
              em vez de calcular o tributo da operação, aplica-se um{" "}
              <strong className="text-tinta">percentual cadastrado para aquele fornecedor</strong> —
              o que a Receita estima que ele deve, na média. O fornecedor recebe o valor da
              operação menos essa retenção, e acerta o saldo na apuração.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-cinza">
              O regime começa em <strong className="text-tinta">2027</strong>, junto com a
              cobrança da CBS. Datas anteriores são recusadas pela própria Receita.
            </p>
          </Bloco>
        </div>

        <div className="lg:sticky lg:top-24 lg:self-start" ref={resultadoRef}>
          <div className="rounded-cf-lg border border-borda bg-superficie p-5">
            <h2 className="font-display text-base font-bold text-titulo">Resultado</h2>

            <div className="mt-3">
              <Rotulo para="sp-rotulo">Nome deste cálculo (opcional)</Rotulo>
              <input
                id="sp-rotulo"
                className={CAMPO_CLARO}
                placeholder="Pagamento ao fornecedor Y"
                maxLength={120}
                value={rotulo}
                onChange={(e) => setRotulo(e.target.value)}
              />
            </div>

            <button
              type="button"
              className="pill-cta mt-4 w-full disabled:opacity-50"
              disabled={falta.length > 0 || estado.fase === "calculando"}
              onClick={() => void calcular()}
            >
              {estado.fase === "calculando" ? "Calculando…" : "Calcular na Receita"}
            </button>

            {falta.length > 0 && (
              <p className="mt-2 text-xs leading-relaxed text-cinza">
                Falta preencher: {falta.join(", ")}.
              </p>
            )}

            <div className="mt-4 flex flex-col gap-4">
              {estado.fase === "recusa" && (
                <RecusaDoGov titulo={estado.titulo} detalhe={estado.detalhe} />
              )}
              {estado.fase === "indisponivel" && <GovForaDoAr motivo={estado.motivo} />}
              {estado.fase === "pronto" && (
                <>
                  {/* O 200 zerado vem ANTES do número herói de propósito: sem
                      isto o aluno lê "R$ 0,00 retido" como resultado, que foi o
                      que o Bubble mostrava. */}
                  {estado.r.semCadastro ? (
                    <div data-sem-cadastro className="rounded-cf-lg border border-alerta-tinta/40 bg-alerta-fundo p-4">
                      <p className="text-sm font-bold text-alerta-tinta">
                        Este fornecedor não tem percentual cadastrado
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-alerta-tinta">
                        A Receita respondeu com todos os valores zerados — e sem erro, porque
                        para ela "não cadastrado" é retenção zero. Use um dos fornecedores da
                        lista ao lado para ver a retenção acontecendo.
                      </p>
                    </div>
                  ) : (
                    <SeloSimulado codigo={estado.simulado} texto={estado.aviso} />
                  )}

                  <NumeroHeroi
                    rotulo="Retido no pagamento"
                    valor={emReais(estado.r.valorTotalTributos)}
                    apoio={
                      <>
                        O fornecedor recebe{" "}
                        <strong className="text-tinta">{emReais(estado.r.valorFornecedor)}</strong>.
                      </>
                    }
                  />

                  <div>
                    <LinhaDeTributo
                      rotulo="CBS"
                      aliquota={estado.r.aliquotaCbs}
                      valor={estado.r.valorCbs}
                    />
                    <LinhaDeTributo
                      rotulo="IBS Estadual"
                      aliquota={estado.r.aliquotaIbsUf}
                      valor={estado.r.valorIbsUf}
                    />
                    <LinhaDeTributo
                      rotulo="IBS Municipal"
                      aliquota={estado.r.aliquotaIbsMun}
                      valor={estado.r.valorIbsMun}
                    />
                  </div>

                  {!estado.guardado && (
                    <p className="text-xs leading-relaxed text-cinza">
                      {estado.motivoDoRegistro === "historico-cheio"
                        ? "O seu histórico está cheio (500 cálculos). Este resultado não foi guardado — apague alguns na página inicial da ferramenta."
                        : "Este cálculo não foi guardado no seu histórico, mas o resultado acima é válido."}
                    </p>
                  )}
                </>
              )}
              {estado.fase === "vazio" && (
                <p className="text-sm leading-relaxed text-cinza">
                  Escolha um fornecedor da lista, informe o valor da operação e calcule. A
                  resposta vem da calculadora da Receita Federal.
                </p>
              )}
            </div>
          </div>

          <p className="mt-4 text-xs leading-relaxed text-cinza">
            <Link to={`${BASE}${window.location.search}`} className="text-link hover:underline">
              Voltar às três calculadoras
            </Link>
          </p>
        </div>
      </div>
    </>
  );
}

/** O percentual de um cadastrado, como o gov manda (número, não string). */
function formatarPercentual(v: number): string {
  return v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + "%";
}
